import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const [osList, setOsList] = useState([]);
  const [osName, setOsName] = useState("");

  const load = async () => {
    try {
      const [c, o] = await Promise.all([
        api.get("/categories"),
        api.get("/operating-systems"),
      ]);
      setCats(c.data);
      setOsList(o.data);
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const onAddCat = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/categories", { name: name.trim(), description: desc });
      setName(""); setDesc("");
      toast.success("Category added");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const onDeleteCat = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Category removed");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const onAddOs = async (e) => {
    e.preventDefault();
    if (!osName.trim()) return;
    try {
      await api.post("/operating-systems", { name: osName.trim() });
      setOsName("");
      toast.success("OS added");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const onDeleteOs = async (id) => {
    try {
      await api.delete(`/operating-systems/${id}`);
      toast.success("OS removed");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="categories-page">
      <PageHeader
        eyebrow="[03] Taxonomy"
        title="Categories & Operating Systems"
        subtitle="Define how your asset taxonomy is structured."
      />
      <div className="p-8 space-y-10">
        {/* Categories */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <form onSubmit={onAddCat} className="lg:col-span-1 border border-[#E5E0D5] bg-white p-6 space-y-4 self-start" data-testid="category-form">
            <div className="uppercase-label">New Category</div>
            <Input data-testid="category-name-input" placeholder="e.g. Tablet" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            <Input data-testid="category-desc-input" placeholder="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} />
            <Button type="submit" data-testid="add-category-btn" className="w-full rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase">
              <Plus className="w-4 h-4 mr-2" /> Add Category
            </Button>
          </form>

          <div className="lg:col-span-2 border border-[#E5E0D5] bg-white">
            <div className="px-6 py-3 border-b border-[#E5E0D5]">
              <div className="uppercase-label">Categories ({cats.length})</div>
            </div>
            <div className="divide-y divide-[#E5E0D5]">
              {cats.length === 0 && <div className="px-6 py-6 text-xs font-mono text-[#9A9A9A]">No categories defined.</div>}
              {cats.map((c) => (
                <div key={c.id} data-testid={`category-row-${c.id}`} className="px-6 py-4 flex items-center justify-between hover:bg-[#F0EDE3]">
                  <div>
                    <div className="font-mono text-sm text-[#0F0F0F]">{c.name}</div>
                    {c.description && <div className="text-xs text-[#7A7A7A] mt-0.5">{c.description}</div>}
                  </div>
                  <button onClick={() => onDeleteCat(c.id)} data-testid={`delete-category-${c.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Operating Systems */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <form onSubmit={onAddOs} className="lg:col-span-1 border border-[#E5E0D5] bg-white p-6 space-y-4 self-start" data-testid="os-form">
            <div className="uppercase-label">New Operating System</div>
            <Input data-testid="os-name-input" placeholder="e.g. Windows 12" value={osName} onChange={(e) => setOsName(e.target.value)} className={inputCls} />
            <Button type="submit" data-testid="add-os-btn" className="w-full rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase">
              <Plus className="w-4 h-4 mr-2" /> Add OS
            </Button>
          </form>

          <div className="lg:col-span-2 border border-[#E5E0D5] bg-white">
            <div className="px-6 py-3 border-b border-[#E5E0D5]">
              <div className="uppercase-label">Operating Systems ({osList.length})</div>
            </div>
            <div className="divide-y divide-[#E5E0D5]">
              {osList.length === 0 && <div className="px-6 py-6 text-xs font-mono text-[#9A9A9A]">No operating systems defined.</div>}
              {osList.map((o) => (
                <div key={o.id} data-testid={`os-row-${o.id}`} className="px-6 py-4 flex items-center justify-between hover:bg-[#F0EDE3]">
                  <div className="font-mono text-sm text-[#0F0F0F]">{o.name}</div>
                  <button onClick={() => onDeleteOs(o.id)} data-testid={`delete-os-${o.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "rounded-none bg-[#FAF7F0] border-[#D6CFC1] font-mono text-sm focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0";
